"use client";

import React, { useMemo } from "react";
import { BlocksRenderer } from "@strapi/blocks-react-renderer";

/**
 * Renders Strapi rich content blocks using BlocksRenderer with optional automatic heading numbering.
 * 
 * @param content - The Strapi blocks content to render
 * @param numbered - Enable automatic heading numbering (default: false)
 * 
 * Heading numbering format (when enabled):
 * - H1: "1. ", "2. ", "3. "
 * - H2: "1.1 ", "1.2 ", "2.1 "
 * - H3+: "1.1.1 ", "1.1.2 ", "2.1.1 "
 * 
 * Example block: { type: 'heading', children: [...], level: 1 }
 */
type TextNode = { type: "text"; text: string };
type Block = { type: string; level?: number; children?: Array<TextNode | Record<string, unknown>> };
type BlocksContent = React.ComponentProps<typeof BlocksRenderer>["content"];

export default function AppBlocks({ content, numbered = false }: { content: unknown; numbered?: boolean }) {
  console.debug("AppBlocks content:", content);

  const contentArray = useMemo<Block[]>(
    () => (Array.isArray(content) ? (content as Block[]) : []),
    [content]
  );
  // no-op: we avoid JSON.stringify in deps now; using typed array instead

  // Pure, deterministic transform: compute numbering from input blocks with no render-time mutation
  const numberedContent = useMemo<unknown>(() => {
    if (!numbered) return contentArray;

    // Deep clone via JSON since Strapi blocks are JSON-serializable
  const cloned: Block[] = JSON.parse(JSON.stringify(contentArray)) as Block[];

    const counters = [0, 0, 0, 0, 0, 0]; // h1..h6

    const prefixHeading = (level: number): string => {
      // Increment current level
      const idx = Math.max(1, Math.min(6, level)) - 1;
      counters[idx] += 1;
      // Reset deeper levels
      for (let i = idx + 1; i < 6; i++) counters[i] = 0;
      // Build label up to current depth (ignore trailing zeros)
      const parts: number[] = [];
      for (let i = 0; i <= idx; i++) parts.push(counters[i]);
      return parts.join(".") + ". ";
    };

    // Walk only top-level blocks in order; prefix heading text nodes
    const isTextNode = (node: unknown): node is TextNode => {
      if (!node || typeof node !== "object") return false;
      const n = node as { type?: unknown; text?: unknown };
      return n.type === "text" && typeof n.text === "string";
    };

    for (const block of cloned) {
      if (block?.type === "heading" && typeof block.level === "number") {
        const label = prefixHeading(block.level);
        const children: Array<TextNode | Record<string, unknown>> = Array.isArray(block.children) ? block.children : [];
        // Find first text node child
        const firstTextIndex = children.findIndex((c) => isTextNode(c));
        if (firstTextIndex >= 0) {
          (children[firstTextIndex] as TextNode).text = label + (children[firstTextIndex] as TextNode).text;
        } else {
          // Prepend a new text node if none exists
          children.unshift({ type: "text", text: label } as TextNode);
        }
        block.children = children;
      }
    }

    return cloned as unknown;
  }, [contentArray, numbered]);

  return (
    <BlocksRenderer
      content={numberedContent as BlocksContent}
      blocks={{
        heading: ({ children, level }: { children?: React.ReactNode; level: number }) => {
          if (level === 1) {
            return (
              <h1 className="text-3xl font-extrabold mb-4">
                {children}
              </h1>
            );
          }
          if (level === 2) {
            return (
              <h2 className="text-2xl font-semibold mb-3">
                {children}
              </h2>
            );
          }
          return (
            <div className="font-bold mb-2">
              {children}
            </div>
          );
        },
        list: ({ children, format }) =>
          format === "ordered"
          ? <ol className="list-decimal pl-6 mb-4 last:mb-0 opacity-90">{children}</ol>
          : <ul className="list-disc pl-6 mb-4 last:mb-0 opacity-90">{children}</ul>,
      }}
    />
  );
}
